import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse, StudentEffortAnalysisData } from "@/types/global";
import { LeaderboardCard, RewardCoins } from "@/types/studentDashboard";
import {
  ActivityWiseRecommendations,
  OverviewCard,
  PerformanceCard,
  PerformanceCards,
  PerformanceTrendsData,
} from "@/types/userMetrics";

export default class UserMetricsApi extends ApiVerified {
  async getStudentSubjectOverviewCards(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number,
    isDemoUser: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OverviewCard>>(
        `user-metrics/get-student-overview-cards`,
        {
          params: {
            userId: userId,
            courseId: courseId,
            classId: classId,
            subjectId: subjectId,
            isDemoUser: isDemoUser,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchStudentOverviewCards(
    userId: number,
    batchId: number,
    subjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OverviewCard>>(
        `user-metrics/get-batch-student-overview-cards`,
        { params: { userId: userId, batchId: batchId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentChapterwiseCards(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number,
    includeAllChapters: boolean,
    isDemoUser: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PerformanceCards>(
        `user-metrics/get-student-chapterwise-cards`,
        {
          params: {
            userId: userId,
            courseId: courseId,
            classId: classId,
            subjectId: subjectId,
            includeAllChapters: includeAllChapters,
            isDemoUser: isDemoUser,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchStudentChapterWiseCards(
    userId: number,
    batchId: number,
    subjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PerformanceCards>(
        `user-metrics/get-batch-student-chapterwise-cards`,
        { params: { userId: userId, batchId: batchId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentRecommendations(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number | null,
    activityName: string | null,
    isDemoUser: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ActivityWiseRecommendations>(
        `user-metrics/get-student-recommendation-cards`,
        {
          UserId: userId,
          CourseId: courseId,
          ClassId: classId,
          SubjectId: subjectId,
          ActivityNames: activityName ? [activityName] : null,
          IsDemoUser: isDemoUser,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentLeaderboard(
    userId: number,
    courseId: number,
    classId: number,
    isDemoUser: boolean,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LeaderboardCard>>(
        `user-metrics/get-student-leaderboard`,
        {
          params: {
            userId: userId,
            courseId: courseId,
            classId: classId,
            isDemoUser: isDemoUser,
            subjectId: subjectId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getRewardCoinsForStudent(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<RewardCoins>(
        `user-metrics/get-student-reward-coins`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentSubjectTrends(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PerformanceTrendsData>(
        `user-metrics/get-student-subject-trends`,
        { params: { userId: userId, courseId: courseId, classId: classId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentSubjectCards(
    userId: number,
    courseId: number,
    classId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PerformanceCard>>(
        `user-metrics/get-student-subject-cards`,
        { params: { userId: userId, courseId: courseId, classId: classId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentEffortAnalysis(
    userId: number,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentEffortAnalysisData>(
        `user-metrics/student-effort-analysis`,
        { params: { userId: userId, startDate: startDate, endDate: endDate } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
